/**
 * 認証関連のバリデーション、定数、ユーティリティ関数
 * API呼び出しやビジネスロジックは含まない純粋な関数群
 */

// ■ バリデーション関数
export const validators = {
  /**
   * メールアドレスの形式をチェック
   * @param {string} email - 検証するメールアドレス
   * @returns {boolean} 有効な形式かどうか
   */
  email: (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email),
  
  /**
   * 必須項目のチェック（空文字・null・undefined・空白のみを除外）
   * @param {any} value - 検証する値
   * @returns {boolean} 有効な値かどうか
   */
  required: (value) => value && value.trim().length > 0,
  
  /**
   * 認証コードの形式をチェック（6桁の数字）
   * @param {string} code - 検証する認証コード
   * @returns {boolean} 有効な形式かどうか
   */
  verificationCode: (code) => code.length === 6 && /^\d{6}$/.test(code)
};

// ■ エラーメッセージ定数
export const ERROR_MESSAGES = {
  // バリデーション関連
  EMAIL_REQUIRED: 'メールアドレスを入力してください',
  EMAIL_INVALID: '有効なメールアドレスを入力してください',
  CODE_REQUIRED: '認証コードを入力してください',
  CODE_INVALID: '認証コードは6桁の数字で入力してください',
  
  // API関連
  SEND_FAILED: '認証コードの送信に失敗しました。しばらく時間をおいてからお試しください。',
  VERIFY_FAILED: '認証コードが正しくありません。再度確認してください。',
  RESEND_FAILED: '再送信に失敗しました。しばらく時間をおいてからお試しください。',
  SYSTEM_ERROR: 'システムエラーが発生しました',
  NETWORK_ERROR: 'ネットワークエラーが発生しました。接続を確認してください。',
  TIMEOUT_ERROR: 'リクエストがタイムアウトしました。もう一度お試しください。'
};

// ■ 成功メッセージ定数
export const SUCCESS_MESSAGES = {
  CODE_SENT: '認証コードを送信しました',
  CODE_RESENT: '認証コードを再送信しました',
  AUTH_COMPLETE: '認証が完了しました'
};

// ■ ステップ設定定数
export const STEPS = {
  EMAIL_INPUT: 1,
  CODE_VERIFICATION: 2,
  SUCCESS: 3
};

// ■ ステップ情報（UI表示用）
export const STEP_INFO = {
  [STEPS.EMAIL_INPUT]: {
    icon: '📧',
    title: 'ステップ 1/3: メールアドレス入力',
    description: '認証コードを送信するメールアドレスを入力してください'
  },
  [STEPS.CODE_VERIFICATION]: {
    icon: '🔑',
    title: 'ステップ 2/3: 認証コード入力',
    description: 'メールが届かない場合は、迷惑メールフォルダもご確認ください。'
  },
  [STEPS.SUCCESS]: {
    icon: '✅',
    title: 'ステップ 3/3: 認証完了',
    description: '次のステップでお客様情報を入力してください。'
  }
};

// ■ ユーティリティ関数

/**
 * プログレスバーの幅を計算
 * @param {number} step - 現在のステップ番号
 * @returns {string} CSS用の幅パーセンテージ
 */
export const getProgressWidth = (step) => {
  const maxSteps = Object.keys(STEPS).length;
  return `${(step / maxSteps) * 100}%`;
};

/**
 * バリデーションエラーを生成するヘルパー関数
 * @param {string} fieldName - フィールド名
 * @param {any} value - 検証する値
 * @returns {string|null} エラーメッセージまたはnull
 */
export const validateField = (fieldName, value) => {
  // 必須チェック
  if (!validators.required(value)) {
    return fieldName === 'email' ? ERROR_MESSAGES.EMAIL_REQUIRED : ERROR_MESSAGES.CODE_REQUIRED;
  }
  
  // フィールド固有のバリデーション
  if (fieldName === 'email' && !validators.email(value)) {
    return ERROR_MESSAGES.EMAIL_INVALID;
  }
  
  if (fieldName === 'verificationCode' && !validators.verificationCode(value)) {
    return ERROR_MESSAGES.CODE_INVALID;
  }
  
  return null;
};

/**
 * デバウンス付きバリデーター関数を作成
 * @param {Function} validatorFn - バリデーション関数
 * @param {string} errorMessage - エラーメッセージ
 * @returns {Function} バリデーション結果を返す関数
 */
export const createValidator = (validatorFn, errorMessage) => {
  return (value) => {
    if (!validatorFn(value)) {
      return { isValid: false, message: errorMessage };
    }
    return { isValid: true, message: '' };
  };
};

/**
 * フォーム入力値を正規化するハンドラー群
 */
export const inputHandlers = {
  /**
   * メールアドレス入力の正規化（そのまま返す）
   * @param {string} value - 入力値
   * @param {Function} onChange - 変更時のコールバック
   */
  email: (value, onChange) => onChange(value),
  
  /**
   * 認証コード入力の正規化（数字のみを許可）
   * @param {string} value - 入力値
   * @param {Function} onChange - 変更時のコールバック
   */
  verificationCode: (value, onChange) => {
    const filtered = value.replace(/[^0-9]/g, '');
    onChange(filtered);
  }
};

/**
 * ステップの妥当性をチェック
 * @param {number} step - チェックするステップ番号
 * @returns {boolean} 有効なステップかどうか
 */
export const isValidStep = (step) => {
  return Object.values(STEPS).includes(step);
};

/**
 * 次のステップを取得
 * @param {number} currentStep - 現在のステップ
 * @returns {number|null} 次のステップまたはnull
 */
export const getNextStep = (currentStep) => {
  const steps = Object.values(STEPS).sort((a, b) => a - b);
  const currentIndex = steps.indexOf(currentStep);
  return currentIndex >= 0 && currentIndex < steps.length - 1 ? steps[currentIndex + 1] : null;
};

/**
 * 前のステップを取得
 * @param {number} currentStep - 現在のステップ
 * @returns {number|null} 前のステップまたはnull
 */
export const getPreviousStep = (currentStep) => {
  const steps = Object.values(STEPS).sort((a, b) => a - b);
  const currentIndex = steps.indexOf(currentStep);
  return currentIndex > 0 ? steps[currentIndex - 1] : null;
};

/**
 * エラーの種類を判定
 * @param {Error} error - エラーオブジェクト
 * @returns {string} エラーの種類
 */
export const getErrorType = (error) => {
  if (error.name === 'AbortError' || error.message.includes('timeout')) {
    return 'TIMEOUT_ERROR';
  }
  if (error.message.includes('network') || error.message.includes('fetch')) {
    return 'NETWORK_ERROR';
  }
  return 'SYSTEM_ERROR';
};

/**
 * エラーメッセージを取得
 * @param {Error} error - エラーオブジェクト
 * @returns {string} 適切なエラーメッセージ
 */
export const getErrorMessage = (error) => {
  const errorType = getErrorType(error);
  return ERROR_MESSAGES[errorType] || ERROR_MESSAGES.SYSTEM_ERROR;
};